// web/src/components/stock/StockTable.tsx
import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useRef, useState, type MouseEvent } from "react";
import type { StockRow } from "../../lib/api";

/**
 * Таблица раздела.
 *
 * «Не учтено» — ОТДЕЛЬНАЯ колонка, а не подпись рядом с расчётным остатком:
 * два числа в одной ячейке владелец уже однажды прочитал как одно.
 *
 * «Обещано WB» стоит рядом с расчётом намеренно: один физический короб виден
 * обеим площадкам, и двойное обещание должно быть видно глазами, а не
 * прятаться внутри арифметики.
 *
 * Числа моноширинные (`tabular-nums`) и прижаты вправо: столбец цифр читают
 * сравнением, а не чтением, и прыгающая ширина знака его ломает.
 *
 * `data-annot` стоит на строке (артикулом) и на каждой ячейке (названием
 * колонки): по ним аннотатор собирает адрес «таблица › строка 968-M › Расчёт».
 * Без них замечание о колонке приходит как `td.px-2.text-right` — четырнадцать
 * колонок, и все с этим адресом.
 *
 * Колонок с заголовком «FBS» ДВЕ — число и вкл/выкл. В адресе они разведены
 * («FBS» и «статус FBS»): одинаковый адрес у двух разных колонок сделал бы
 * замечание неразличимым ровно там, где его пишут чаще всего.
 */
const NUM = "px-2 text-right tabular-nums";
// Строка 38px — та же величина, что в `h-[38px]` ниже. Виртуализатор обязан
// знать её ЧИСЛОМ: он считает высоту списка до того, как строки нарисованы.
const ROW = 38;

// Ключ колонки города — `city:<id>`, остальных — имя поля `StockRow`. `null`
// — сортировки нет, порядок каталога как есть.
export type Sort = { key: string; dir: "asc" | "desc" } | null;

// Кликабельный заголовок колонки — один компонент вместо повторения
// разметки клика/стрелки в каждом `<th>`.
function Head({
  label,
  sortKey,
  annot,
  align = "right",
  sort,
  onSort,
}: {
  label: string;
  sortKey: string;
  annot: string;
  align?: "left" | "right";
  sort: Sort;
  onSort: (key: string) => void;
}) {
  const active = sort?.key === sortKey;
  return (
    <th
      className={
        (align === "right" ? NUM : "px-2 text-left") +
        " cursor-pointer select-none font-normal hover:text-fg"
      }
      onClick={() => onSort(sortKey)}
      title="Клик — сортировка по этому столбцу"
      data-annot={annot}
    >
      {label}
      {active && (
        <span className="ml-1">{sort.dir === "desc" ? "▼" : "▲"}</span>
      )}
    </th>
  );
}

export function StockTable({
  rows,
  selected,
  onToggle,
  onToggleAll,
  cities,
  sort,
  onSort,
  onFilter,
}: {
  rows: StockRow[];
  selected: Set<number>;
  onToggle: (id: number) => void;
  onToggleAll: () => void;
  cities: { id: string; label: string }[];
  sort: Sort;
  onSort: (key: string) => void;
  onFilter: (kind: "model" | "card" | "status", value: string) => void;
}) {
  const allChosen =
    rows.length > 0 && rows.every((r) => selected.has(r.ozon_product_id));

  // Увеличенная миниатюра — ОТ МИНИАТЮРЫ, а не от центра экрана: по центру
  // она закрывала ровно ту строку, которую наводят, чтобы рассмотреть.
  // `fixed` остаётся обязательным — прокручиваемый контейнер таблицы иначе
  // обрезал бы её по границе `overflow: auto`.
  const [hoverImage, setHoverImage] = useState<{
    src: string;
    left: number;
    top: number;
  } | null>(null);
  const ZOOM = 220;

  function showZoom(event: MouseEvent<HTMLImageElement>, src: string) {
    const rect = event.currentTarget.getBoundingClientRect();
    const gap = 6;
    // Справа не помещается — уходим влево от миниатюры.
    const left =
      rect.right + gap + ZOOM <= window.innerWidth
        ? rect.right + gap
        : Math.max(gap, rect.left - gap - ZOOM);
    const top = Math.min(rect.top, window.innerHeight - ZOOM - gap);
    setHoverImage({ src, left, top });
  }

  // Тот же разрыв, что и с прокруткой: `rows` меняется под наведённым
  // курсором (сменился фильтр, поиск, сортировка) без единого движения
  // мыши — координаты наведённой миниатюры больше не значат ту же строку.
  // Гасим тем же сбросом, а не заводим второй механизм.
  useEffect(() => {
    setHoverImage(null);
  }, [rows]);

  const scrollRef = useRef<HTMLDivElement>(null);
  // Рисуем только видимые строки: каталог — 809 строк по 14+ колонок, и
  // целиком это одиннадцать тысяч ячеек в DOM.
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW,
    overscan: 12,
  });
  const items = virtualizer.getVirtualItems();
  // Место сверху и снизу занимают ДВЕ пустые строки, а не абсолютное
  // позиционирование: так таблица остаётся таблицей, шапка — липкой, а
  // ширины колонок считает браузер.
  const padTop = items.length ? items[0]!.start : 0;
  const padBottom = items.length
    ? virtualizer.getTotalSize() - items[items.length - 1]!.end
    : 0;

  return (
    <div
      ref={scrollRef}
      className="min-h-0 flex-1 overflow-auto"
      data-annot="таблица"
      // Чистая прокрутка колесом, без движения курсора, не шлёт `mouseleave`
      // с миниатюры — `hoverImage` держит координаты по строке, которая при
      // виртуализации уже сменилась под ними. Раньше строка со своей
      // картинкой размонтировалась вместе с прокруткой, и CSS `:hover`
      // переставал матчить сам; теперь картинка не привязана к DOM строки, и
      // единственный способ погасить её — сбросить состояние по `onScroll`.
      onScroll={() => setHoverImage(null)}
    >
      <table className="w-full table-fixed border-collapse text-[13px]">
        <colgroup>
          <col className="w-9" />
          <col className="w-11" />
          {/* «Товар» и «Баркод» — явная ширина, а не автоширина: `table-fixed`
              нужен виртуализации (иначе ширины прыгали бы при прокрутке), но
              при `table-fixed` браузер сам текст не обрезает — без ширины и
              `truncate` на ячейках артикул с SKU наезжают на соседнюю
              колонку. */}
          <col className="w-[170px]" />
          <col className="w-[120px]" />
          <col className="w-[86px]" />
          <col className="w-[86px]" />
          <col className="w-[76px]" />
          <col className="w-[76px]" />
          <col className="w-[64px]" />
          <col className="w-[64px]" />
          <col className="w-[64px]" />
          <col className="w-[86px]" />
          <col className="w-[80px]" />
          {cities.map((city) => (
            <col key={city.id} className="w-[64px]" />
          ))}
          <col className="w-[86px]" />
          <col className="w-[92px]" />
          <col className="w-[72px]" />
          <col className="w-[80px]" />
        </colgroup>
        <thead
          className="sticky top-0 z-10 bg-bg-subtle text-fg-muted"
          data-annot="шапка таблицы"
        >
          <tr className="h-[38px]">
            <th className="w-9 px-2" data-annot="выбор">
              <input
                type="checkbox"
                checked={allChosen}
                onChange={onToggleAll}
                title="Выбрать всё, что сейчас в таблице"
              />
            </th>
            <th className="px-2 text-left font-normal" data-annot="Фото">
              Фото
            </th>
            <Head
              label="Товар"
              sortKey="offer_id"
              annot="Товар"
              align="left"
              sort={sort}
              onSort={onSort}
            />
            <Head
              label="Баркод"
              sortKey="barcode"
              annot="Баркод"
              align="left"
              sort={sort}
              onSort={onSort}
            />
            <Head
              label="Model ID"
              sortKey="model_id"
              annot="Model ID"
              sort={sort}
              onSort={onSort}
            />
            <Head
              label="Card ID"
              sortKey="card_id"
              annot="Card ID"
              sort={sort}
              onSort={onSort}
            />
            <Head
              label="Показов"
              sortKey="views"
              annot="Показов"
              sort={sort}
              onSort={onSort}
            />
            <Head
              label="Заказано"
              sortKey="orders"
              annot="Заказано"
              sort={sort}
              onSort={onSort}
            />
            <Head
              label="FBO"
              sortKey="fbo"
              annot="FBO"
              sort={sort}
              onSort={onSort}
            />
            <Head
              label="FBS"
              sortKey="fbs"
              annot="FBS"
              sort={sort}
              onSort={onSort}
            />
            <Head
              label="Обещано WB"
              sortKey="promised_wb"
              annot="Обещано WB"
              sort={sort}
              onSort={onSort}
            />
            <Head
              label="Бронь"
              sortKey="booked"
              annot="Бронь"
              sort={sort}
              onSort={onSort}
            />
            <Head
              label="В коробах"
              sortKey="korob_counted"
              annot="В коробах"
              sort={sort}
              onSort={onSort}
            />
            {cities.map((city) => (
              <Head
                key={city.id}
                label={city.label}
                sortKey={`city:${city.id}`}
                annot={`короба ${city.label}`}
                sort={sort}
                onSort={onSort}
              />
            ))}
            <Head
              label="Со складом"
              sortKey="korob_working"
              annot="Со складом"
              sort={sort}
              onSort={onSort}
            />
            <Head
              label="Расчёт"
              sortKey="available"
              annot="Расчёт"
              sort={sort}
              onSort={onSort}
            />
            <Head
              label="Не учтено"
              sortKey="korob_skipped"
              annot="Не учтено"
              sort={sort}
              onSort={onSort}
            />
            <Head
              label="FBS"
              sortKey="fbs_enabled"
              annot="статус FBS"
              align="left"
              sort={sort}
              onSort={onSort}
            />
          </tr>
        </thead>
        <tbody>
          {padTop > 0 && <tr style={{ height: padTop }} aria-hidden />}
          {items.map((item) => {
            const row = rows[item.index]!;
            return (
              <tr
                key={row.ozon_product_id}
                className="h-[38px] border-t border-border hover:bg-bg-hover"
                data-annot={`строка ${row.offer_id}`}
              >
                <td className="px-2" data-annot="выбор">
                  <input
                    type="checkbox"
                    checked={selected.has(row.ozon_product_id)}
                    onChange={() => onToggle(row.ozon_product_id)}
                  />
                </td>
                {/* Миниатюра 28px в строке 38px. `loading="lazy"` обязателен:
                    виртуализация держит в DOM три десятка строк, но при
                    быстрой прокрутке браузер иначе дёргает CDN на каждую.
                    Увеличение рисуется ОДНО на таблицу, по наведению — не
                    CSS-трюком `peer`, а состоянием: адрес всплывающей
                    картинки зависит от координат конкретной миниатюры. */}
                <td className="px-2" data-annot="Фото">
                  {row.image ? (
                    <img
                      src={row.image}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      onMouseEnter={(e) => showZoom(e, row.image!)}
                      onMouseLeave={() => setHoverImage(null)}
                      className="h-7 w-7 rounded-[3px] border border-border object-cover"
                    />
                  ) : (
                    <div className="h-7 w-7 rounded-[3px] border border-border bg-bg-subtle" />
                  )}
                </td>
                {/* Название — в `title`, а не в ячейке: ширины в таблице оно
                    занимает много, а отвечает на вопрос «что это» и по
                    наведению. `truncate` — по той же причине, что и ширина
                    колонки в colgroup: без обрезки текст выходит за 170px и
                    наезжает на «Баркод». В `title` — ещё и то, что обрезалось
                    (артикул и SKU), не только название. */}
                <td
                  className="truncate px-2 text-fg"
                  title={`${row.offer_id} · ${row.sku} — ${row.name}`}
                  data-annot="Товар"
                >
                  {row.offer_id}
                  <span className="ml-2 text-fg-faint">{row.sku}</span>
                </td>
                <td
                  className="truncate px-2 tabular-nums text-fg-faint"
                  title={
                    row.card_id
                      ? `Карточка CRM ${row.card_id} — найдена по этому баркоду`
                      : "Карточка CRM по баркоду не нашлась: коробов у строки не будет"
                  }
                  data-annot="Баркод"
                >
                  {row.barcode ?? "—"}
                </td>
                <td className={NUM} data-annot="Model ID">
                  {row.model_id ? (
                    <button
                      type="button"
                      onClick={() => onFilter("model", String(row.model_id))}
                      className="tabular-nums text-fg-muted underline decoration-dotted hover:text-fg"
                      title={
                        row.model_name
                          ? `${row.model_name} — показать все размеры модели`
                          : "Показать все размеры модели"
                      }
                    >
                      {row.model_id}
                    </button>
                  ) : (
                    <span className="text-fg-faint">—</span>
                  )}
                </td>
                <td className={NUM} data-annot="Card ID">
                  {row.card_id ? (
                    <button
                      type="button"
                      onClick={() => onFilter("card", String(row.card_id))}
                      className="tabular-nums text-fg-muted underline decoration-dotted hover:text-fg"
                      title="Показать только эту карточку CRM"
                    >
                      {row.card_id}
                    </button>
                  ) : (
                    <span className="text-fg-faint">—</span>
                  )}
                </td>
                <td className={NUM} data-annot="Показов">
                  {row.views}
                </td>
                <td className={NUM} data-annot="Заказано">
                  {row.orders}
                </td>
                <td className={NUM} data-annot="FBO">
                  {row.fbo}
                </td>
                <td className={NUM + " text-fg"} data-annot="FBS">
                  {row.fbs}
                </td>
                <td className={NUM + " text-fg-faint"} data-annot="Обещано WB">
                  {row.promised_wb}
                </td>
                <td className={NUM + " text-fg-faint"} data-annot="Бронь">
                  {row.booked}
                </td>
                <td className={NUM} data-annot="В коробах">
                  {row.korob_counted}
                </td>
                {cities.map((city) => (
                  <td
                    key={city.id}
                    className={NUM + " text-fg-faint"}
                    data-annot={`короба ${city.label}`}
                  >
                    {/* Ноль рисуем прочерком: столбец нулей — шум в таблице,
                        которую читают глазами. */}
                    {row.korob[city.id] ?? "—"}
                  </td>
                ))}
                <td
                  className={NUM}
                  title="Короба в городах, где у нас есть склад FBS"
                  data-annot="Со складом"
                >
                  {row.korob_working}
                </td>
                <td className={NUM + " text-fg"} data-annot="Расчёт">
                  {/* Пометка стоит В ячейке, а не отдельной колонкой: ноль без
                      причины владелец прочитал бы как пустой короб. Колонка
                      92px — отсюда короткое «полка N» и полный текст в title. */}
                  {row.shelf_blocked && (
                    <span
                      className="mr-1 text-accent-text"
                      title={`Платье лежит на складе Ozon: свободно ${row.ozon_shelf} шт — на FBS уходит 0`}
                      data-annot="полка Ozon"
                    >
                      полка {row.ozon_shelf}
                    </span>
                  )}
                  {row.available}
                </td>
                <td className={NUM + " text-fg-faint"} data-annot="Не учтено">
                  {row.korob_skipped}
                </td>
                <td className="px-2" data-annot="статус FBS">
                  <button
                    type="button"
                    onClick={() =>
                      onFilter("status", row.fbs_enabled ? "on" : "off")
                    }
                    className={row.fbs_enabled ? "text-ok" : "text-fg-faint"}
                    title="Отобрать товары с этим статусом"
                  >
                    {row.fbs_enabled ? "вкл" : "выкл"}
                  </button>
                </td>
              </tr>
            );
          })}
          {padBottom > 0 && <tr style={{ height: padBottom }} aria-hidden />}
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={17 + cities.length}
                className="px-2 py-6 text-center text-fg-faint"
              >
                По этому отбору строк нет.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {/* Всплывающее увеличение — ОДНО на таблицу, не по строке: адрес
          (`left`/`top`) уже несёт координаты нужной миниатюры. `loading="lazy"`
          здесь работает вместе с тем, что элемент вообще не существует в DOM
          до наведения — картинка не грузится, пока скрыта. */}
      {hoverImage && (
        <img
          src={hoverImage.src}
          alt=""
          aria-hidden
          loading="lazy"
          style={{ left: hoverImage.left, top: hoverImage.top }}
          className="pointer-events-none fixed z-50 h-[220px] w-[220px] rounded-[var(--radius-ui)]
                   border border-border-strong bg-bg object-contain shadow-lg"
        />
      )}
    </div>
  );
}
